import { Compass, LogOut, Moon, Sparkles, UserRound } from "lucide-react";
import { useNavigate } from "react-router";

import { useLogout } from "@/api/queries/auth";
import type { Me } from "@/api/types";
import { Avatar, Menu, MenuItem, MenuLabel, MenuRadioGroup, MenuSeparator } from "@/components/ui";
import { useUserPreference } from "@/hooks/usePreferences";

export function UserMenu({
  user,
  profilePath,
  onOpenWhatsNew,
}: {
  user: Me;
  profilePath: string;
  onOpenWhatsNew?: () => void;
}) {
  const navigate = useNavigate();
  const logout = useLogout();
  const [theme, setTheme] = useUserPreference("theme");
  return (
    <Menu
      side="right"
      align="end"
      label="Account"
      trigger={
        <button
          type="button"
          data-tour="profile-menu"
          aria-label={`Account menu for ${user.full_name || user.email}`}
          className="flex size-9 items-center justify-center rounded-md hover:bg-surface-hover"
        >
          <Avatar name={user.full_name || user.email} src={user.avatar_url} size="sm" />
        </button>
      }
    >
      <div className="px-2 py-1.5">
        <div className="truncate text-sm font-medium text-fg">{user.full_name || user.email}</div>
        <div className="truncate text-xs text-fg-subtle">{user.email}</div>
      </div>
      <MenuSeparator />
      <MenuItem icon={<UserRound />} onSelect={() => navigate(profilePath)}>
        Profile & preferences
      </MenuItem>
      <MenuItem icon={<Compass />} onSelect={() => navigate(`${profilePath}?tab=tours`)}>
        Product tours
      </MenuItem>
      {onOpenWhatsNew && (
        <MenuItem icon={<Sparkles />} onSelect={onOpenWhatsNew}>
          What's new
        </MenuItem>
      )}
      <MenuSeparator />
      <MenuLabel>
        <span className="inline-flex items-center gap-1.5">
          <Moon aria-hidden className="size-3.5" /> Theme
        </span>
      </MenuLabel>
      <MenuRadioGroup
        value={theme}
        onValueChange={setTheme}
        options={[
          { value: "system", label: "Match system" },
          { value: "light", label: "Light" },
          { value: "dark", label: "Dark" },
        ]}
      />
      <MenuSeparator />
      <MenuItem icon={<LogOut />} onSelect={() => logout.mutate()}>
        Sign out
      </MenuItem>
    </Menu>
  );
}
